import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Infinity } from "lucide-react";

const benefits = [
  "Unlimited study sessions",
  "24/7 access to all rooms",
  "No hidden fees or charges",
  "Join any stream instantly",
  "Connect with global community",
  "Premium features included",
];

export const StudyStream = () => {
  return (
    <section className="py-24 relative overflow-hidden" aria-labelledby="studystream-title">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" aria-hidden="true" />
      <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[100px] animate-glow-pulse" aria-hidden="true" />
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-[100px] animate-glow-pulse" style={{ animationDelay: '1.5s' }} aria-hidden="true" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="border-2 border-border bg-card/50 backdrop-blur-xl overflow-hidden rounded-lg">
            <div className="grid lg:grid-cols-2 gap-12 p-8 sm:p-12 lg:p-16">
              {/* Left Content */}
              <article className="space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                  <Infinity className="w-4 h-4 text-primary" aria-hidden="true" />
                  <span className="text-sm font-semibold text-primary">NO LIMIT</span>
                </div>
                
                <header className="space-y-4">
                  <h1 id="studystream-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                    Study <span className="text-gradient">Stream</span>
                  </h1>
                  <p className="text-2xl text-muted-foreground font-light">
                    Do not pay to be productive
                  </p>
                </header>
                
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Join unlimited streams, anytime. Connect with thousands of students worldwide in our virtual study rooms. Stay focused, motivated, and productive - completely free.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Free Stream
                    </a>
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Learn More
                    </a>
                  </Button>
                </div>
              </article>
              
              {/* Right Benefits */}
              <aside className="space-y-6 animate-fade-in" style={{ animationDelay: '0.2s' }} aria-label="Study Stream benefits">
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-foreground mb-6">
                    Everything you need, <span className="text-gradient">free forever</span>
                  </h3>
                  
                  <ul className="space-y-3" aria-label="Benefits list">
                    {benefits.map((benefit, index) => (
                      <li 
                        key={benefit}
                        className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors animate-slide-in"
                        style={{ animationDelay: `${0.3 + index * 0.05}s` }}
                      >
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center" aria-hidden="true">
                          <Check className="w-4 h-4 text-primary-foreground" />
                        </div>
                        <span className="text-foreground font-medium">
                          {benefit}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Stats Card */}
                <figure className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-lg">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-3xl font-bold text-gradient mb-1">∞</p>
                      <p className="text-sm text-muted-foreground">Streams Available</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-gradient mb-1">$0</p>
                      <p className="text-sm text-muted-foreground">Cost to You</p>
                    </div>
                  </div>
                  <figcaption className="sr-only">Study Stream offers unlimited streams at zero cost</figcaption>
                </figure>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
